/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
// import { useEffect, useState } from 'react';

import { AudienceType, AudiencesType, DocumentVersionType } from '../../../algorithm/types';
import { isTeamsUser, useIsConsumerWebUser, useNewFeedbackUserFlag } from '../../../permissions';
import { DocumentVersionCommentButton } from '../../DocumentVersionFeedback/DocumentVersionComment';
import LabeledSwitch from '../LabeledSwitch';
import UserFeedbackButton from '../UserFeedbackButton';
import { selectCreatorMode } from '../../chatSlice';
import styles from './AudienceComponent.module.scss';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { selectShowFeedback, toggleFeedback } from '../../DocumentVersionFeedback/feedbackSlice';
import FeedbackIcon from '../../DocumentVersionFeedback/FeedbackIcon';
import commonStyles from '../MessageToolbar/MessageToolbarCommon.module.scss';
import { AnalyticsDebug } from '../../../analytics/AnalyticsDebug';
// import MedPearlIcon from './MedPearl_logo.svg';
// import MedPearlIconWithoutText from './MedPearl_logo_without_text.svg';
import { OrgViewType, selectOrgView, selectPearlUserInfo, setOrgView } from '../../../Login/userSlice';
import FhirPatientComponent from '../FhirPatientComponent';
import { selectPatientContext } from '../../../Fhir/fhirSlice';
import RoundedColorSelect from '../RoundedColorSelect';
import { useEffect } from 'react';
import GuideNavigationButtons from './GuideNavigationButtons';

type AudienceComponentProps = {
  fixedAudiences?: AudiencesType /* Used in Algorithm view, don't show a drop down, but just show these fixed values instead */;
  compact?: boolean;
  documentVersion?: DocumentVersionType;
  publishedOnly: boolean;
  showPublishedOnly?: boolean;
  showFeedback?: boolean;
  onChangeAudience?: (audience: AudienceType) => void;
  onSetPublishedOnly?: (publishedOnly: boolean) => void;
};

const AudienceComponent = ({
  fixedAudiences,
  compact,
  publishedOnly,
  showPublishedOnly,
  onChangeAudience,
  onSetPublishedOnly,
  documentVersion,
}: AudienceComponentProps) => {
  const dispatch = useAppDispatch();
  // Get patient info session is in EHR launch context
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const isWebConsumer = useIsConsumerWebUser();
  // const showPatientSummary = usePatientSummary(); // show generate patient summary button, date filter and data disclaimer if patient summary is enabled.
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const orgView = useAppSelector(selectOrgView);
  const viewFeedback = !isCreatorMode && isTeamsUser(pearlUserInfo);
  // const navigate = useNavigate();
  // const location = useLocation();
  const patientContext = useAppSelector(selectPatientContext);

  // const handleLogoClick = () => {
  //   return navigate('/home#_');
  // };
  // const [_, setWindowWidth] = useState(window.innerWidth);
  // useEffect(() => {
  //   const handleResize = () => {
  //     setWindowWidth(window.innerWidth);
  //   };
  //   window.addEventListener('resize', handleResize);

  //   return () => {
  //     window.removeEventListener('resize', handleResize);
  //   };
  // }, []);

  useEffect(() => {
    if (isCreatorMode) {
      dispatch(setOrgView('Full View'));
    }
  }, [dispatch, isCreatorMode]);

  if (isWebConsumer) return null;

  return (
    <Stack
      direction="row"
      flexWrap={compact ? undefined : 'wrap'}
      spacing={compact ? 0 : 0.5}
      className={styles.container}
      data-testid="audienceComponent"
      data-id="audienceComponent"
    >
      <GuideNavigationButtons currentGuideTitle={documentVersion?.title} />
      {showPublishedOnly && (
        <Box className={styles.labelledSwitchBox}>
          <LabeledSwitch
            label="Published Only"
            id="audience_publishedonly"
            checked={publishedOnly}
            disabled={onSetPublishedOnly == null}
            onSetCheckedState={onSetPublishedOnly}
            className={styles.publishedOnlySwitch}
          />
        </Box>
      )}
      {isCreatorMode && (
        <RoundedColorSelect
          id="patientContextMRN"
          value={orgView || 'Full View'}
          onChange={(e) => {
            dispatch(setOrgView(e?.target.value as OrgViewType));
          }}
          items={[
            { id: 'full_view', name: 'Full View' },
            { id: 'musc_view', name: 'MUSC View' },
            { id: 'prov_view', name: 'Providence View' },
          ]}
          className={styles.orgViewSelect}
        />
      )}
      <AnalyticsDebug />
      <div className={styles.audienceInformation}></div>
      <div
        className={styles.fhirandFeedbackSection}
        data-testid="patientContextSection"
        data-id="patientContextSection"
      >
        {isCreatorMode && <FhirPatientComponent patientContext={patientContext} />}
        {viewFeedback && <FeedbackButton compact={compact} documentVersion={documentVersion} />}
      </div>
    </Stack>
  );
};

export function FeedbackButton({
  compact,
  documentVersion,
}: {
  compact?: boolean;
  documentVersion?: DocumentVersionType;
}) {
  const viewNewFeedback = useNewFeedbackUserFlag();
  const dispatch = useAppDispatch();
  const showFeedback = useAppSelector(selectShowFeedback);
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const isTeamsAppUser = isTeamsUser(pearlUserInfo);
  const creatorMode = useAppSelector(selectCreatorMode);
  const handleComment = () => {
    //make sure we dont have both feedback and comment active at the same time
    //if no active container exists then show the feedback container and set this to active
    dispatch(toggleFeedback());
  };

  if (viewNewFeedback) {
    return (
      <div
        className={commonStyles.feedbackNewContainer}
        data-isweb-user={!isTeamsAppUser && !creatorMode ? 'true' : 'false'}
      >
        <Divider orientation="vertical" variant="middle" flexItem className={commonStyles.divider} />
        <Button
          className={showFeedback ? commonStyles.digitalAssistantIconActive : commonStyles.digitalAssistantIcon}
          data-testid={`Feedback ${showFeedback ? `Active` : ``}`}
          onClick={handleComment}
          id="feedback-icon"
          style={{ padding: 0 }}
          endIcon={<FeedbackIcon className={commonStyles.digitalAssistantIconButton} />}
        >
          <p className={commonStyles.feedbackLabel}>Feedback</p>
        </Button>
        <DocumentVersionCommentButton documentVersion={documentVersion} />
      </div>
    );
  } else {
    return (
      <Box
        justifyItems="flex-end"
        justifySelf="end"
        className={styles.feedback}
        style={{ marginLeft: !compact ? 10 : 'auto' }}
      >
        <Typography
          sx={{ fontSize: '10px', color: 'var(--gray44)', paddingBottom: '5px' }}
          id="userfeedback_button-label"
        >
          YOUR FEEDBACK IS IMPORTANT
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <UserFeedbackButton />
        </Box>
      </Box>
    );
  }
}

export default AudienceComponent;
